import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { fetchInsight, fetchInsights } from "@/lib/api";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetchInsight(slug).then(setPost).catch(() => setError(true));
    fetchInsights({ limit: 4 }).then((d) => setRelated(d.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <div className="container-x section-y" data-testid="insight-not-found">
        <SectionEyebrow>Not found</SectionEyebrow>
        <h1 className="mt-6 font-display text-4xl tracking-tight">This essay does not exist.</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline-link">
          <ArrowLeft className="h-4 w-4" /> All writing
        </Link>
      </div>
    );
  }

  if (!post) return <div className="container-x section-y text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid={`insight-detail-${post.slug}`}>
      <SEO title={`${post.title} · The Cleanability Institute`} description={post.excerpt} path={`/insights/${post.slug}`} />

      <section className="container-x pt-16 md:pt-24 pb-12">
        <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All writing
        </Link>
        <div className="mt-6 font-mono-mini text-[hsl(var(--accent))]">{post.category} · {post.published_at}</div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-balance max-w-4xl">{post.title}</h1>
        <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
          <div><span className="text-foreground font-medium">{post.author}</span></div>
          <div className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.read_minutes} min read</div>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="max-w-3xl">
          <p className="font-serif-display text-2xl md:text-3xl leading-snug tracking-tight text-balance">{post.excerpt}</p>
          <div className="mt-12 space-y-6 text-base md:text-lg leading-relaxed text-foreground/85 prose-essay" data-testid="insight-body">
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-12 mb-4" {...props} />,
                h2: ({ node, ...props }) => <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-12 mb-3" {...props} />,
                h3: ({ node, ...props }) => <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium mt-10 mb-3" {...props} />,
                p: ({ node, ...props }) => <p className="leading-relaxed" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-2" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-2" {...props} />,
                a: ({ node, ...props }) => <a className="underline-link text-[hsl(var(--accent))]" {...props} />,
                blockquote: ({ node, ...props }) => <blockquote className="font-serif-display text-xl border-l-2 border-[hsl(var(--accent))] pl-6 my-8" {...props} />,
                strong: ({ node, ...props }) => <strong className="text-foreground font-semibold" {...props} />,
              }}
            >
              {post.content || ""}
            </ReactMarkdown>
          </div>
          <div className="mt-16 pt-10 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="font-display text-xl font-medium">{post.author}</div>
              <div className="text-sm text-muted-foreground">The Cleanability Institute</div>
            </div>
            <Link to="/membership" data-testid="insight-cta-membership" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
              Subscribe to the Quarterly <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-border bg-secondary/30">
          <div className="container-x section-y">
            <SectionEyebrow>Continue reading</SectionEyebrow>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link key={p.id} to={`/insights/${p.slug}`} data-testid={`related-insight-${i}`} className="rounded-2xl border border-border bg-card p-7 hover-lift group cursor-pointer">
                  <div className="font-mono-mini text-[hsl(var(--accent))]">{p.category}</div>
                  <div className="mt-4 font-display text-xl font-medium tracking-tight text-balance">{p.title}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
